import i18n, { type BackendModule, type ReadCallback } from 'i18next'
import { initReactI18next } from 'react-i18next'

export const supportedLanguages = ['en', 'zh', 'zh-TW', 'fr', 'ru', 'ja', 'vi'] as const
type SupportedLanguage = (typeof supportedLanguages)[number]
type LocaleModule = {
  default: { translation: Record<string, string> }
}

const localeLoaders: Record<SupportedLanguage, () => Promise<LocaleModule>> = {
  en: () => import('./i18n/locales/en.json'),
  zh: () => import('./i18n/locales/zh.json'),
  'zh-TW': () => import('./i18n/locales/zh-TW.json'),
  fr: () => import('./i18n/locales/fr.json'),
  ru: () => import('./i18n/locales/ru.json'),
  ja: () => import('./i18n/locales/ja.json'),
  vi: () => import('./i18n/locales/vi.json'),
}

const dynamicLocaleBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read: (language: string, _namespace: string, callback: ReadCallback) => {
    const loader = localeLoaders[language as SupportedLanguage]
    if (!loader) {
      callback(new Error(`Unsupported locale: ${language}`), false)
      return
    }
    void loader().then(
      (module) => callback(null, module.default.translation),
      (error: unknown) =>
        callback(
          error instanceof Error ? error : new Error('Unable to load locale'),
          false
        )
    )
  },
}

const savedLanguage = window.localStorage.getItem('computinger-developer-language')
const browserLanguage = navigator.language.toLowerCase()
let initialLanguage: SupportedLanguage = 'en'
if (supportedLanguages.includes(savedLanguage as SupportedLanguage)) {
  initialLanguage = savedLanguage as SupportedLanguage
} else if (browserLanguage.startsWith('zh-tw') || browserLanguage.startsWith('zh-hk')) {
  initialLanguage = 'zh-TW'
} else {
  initialLanguage =
    supportedLanguages.find((language) =>
      browserLanguage.startsWith(language.toLowerCase())
    ) ?? 'en'
}

await i18n.use(dynamicLocaleBackend).use(initReactI18next).init({
  lng: initialLanguage,
  fallbackLng: 'en',
  supportedLngs: [...supportedLanguages],
  load: 'currentOnly',
  interpolation: { escapeValue: false },
  returnNull: false,
})

i18n.on('languageChanged', (language) => {
  document.documentElement.lang = language
  window.localStorage.setItem('computinger-developer-language', language)
})
document.documentElement.lang = i18n.resolvedLanguage ?? i18n.language

export default i18n
